import React, { Fragment, useCallback, useRef } from 'react'
import clsx from 'clsx'
import { graphql } from 'gatsby'
import { Image } from '../../components/image'
import { Accordion } from '../../components/accordion'
import { ContentfulRichText } from '../../components/contentful-rich-text'
import { useItemsObserver } from './useItemsObserver'
import { useSectionsObserver } from './useSectionsObserver'
import {
  Container,
  Link,
  Subtitle,
  Text,
  Title,
  Section,
} from '../../components/page'
import { useSnippets } from '../../components/snippet'

import * as styles from './how-it-works-sections.module.scss'

export type HowItWorksSectionsProps = Queries.HowItWorksSectionsFragment

export const HowItWorksSections = ({ sections }: HowItWorksSectionsProps) => {
  const root = useRef<HTMLDivElement>(null)
  const header = useRef<HTMLDivElement>(null)

  const activeSectionId = useSectionsObserver({ root, header })
  const activeItemId = useItemsObserver({ root, selector: `.${styles.item}` })
  const s = useSnippets()

  // animate scrollIntoView on larger devices
  const onLabelClick = useCallback((e: any) => {
    if (window.innerWidth >= 768) {
      e.preventDefault()
      const { id } = e.currentTarget.dataset
      const section = root.current?.querySelector(`#${id}`)
      section?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  return (
    <div className={styles.sections} ref={root}>
      <div className={styles.header} ref={header}>
        <ol>
          {sections?.map((section) => (
            <li
              key={section?.id}
              className={
                section?.slug === activeSectionId ? styles.activeli : undefined
              }
            >
              <a
                href={`#${section?.slug}`}
                data-id={section?.slug}
                onClick={onLabelClick}
              >
                {section?.label}
              </a>
            </li>
          ))}
        </ol>
      </div>

      {sections?.map((section, index) => {
        const sectionColor = index % 2 ? 'howitworks' : undefined
        const sectionClass = index % 2 ? styles.dark : styles.light
        const textColor = index % 2 ? 'light' : undefined
        const last = (section?.items?.length || 0) - 1
        return (
          <Section
            key={section?.id}
            id={section?.slug!}
            className={`${styles.section} ${sectionClass}`}
            variant={sectionColor}
          >
            <Container className={styles.container}>
              <div className={styles.intro}>
                <Title color={textColor}>{section?.title}</Title>
                <Text color={textColor}>{section?.text?.text}</Text>
              </div>
              <div className={styles.items}>
                {section?.items?.map((item, index) => (
                  <Fragment key={item?.id}>
                    <div
                      data-id={item?.id}
                      data-title={item?.title}
                      className={clsx(
                        styles.item,
                        index === 0 && styles.firstItem,
                        index === last && styles.lastItem
                      )}
                      style={{
                        // set min height to corresponding image height
                        minHeight: item?.image?.gatsbyImageData?.height || 500,
                      }}
                    >
                      <Subtitle color={textColor}>{item?.title}</Subtitle>
                      <Text color={textColor}>{item?.text?.text}</Text>
                      <Link link={item?.link} />
                    </div>
                    <div
                      data-id={item?.id}
                      data-title={item?.title}
                      className={clsx(
                        styles.image,
                        item?.id === activeItemId && styles.activeImage,
                        index === 0 && styles.firstImage,
                        index === last && styles.lastImage
                      )}
                    >
                      <Image
                        image={item?.image}
                        alt={item?.image?.title!}
                        loading="lazy"
                      />
                    </div>
                  </Fragment>
                ))}
              </div>
              {section?.questions && section.questions.length > 0 && (
                <div className={styles.questions}>
                  <div className={styles.qtitle}>
                    <h3>{s.how_it_works_faq}</h3>
                  </div>
                  <Accordion className={styles.accordion} variant={textColor}>
                    {section?.questions?.map((faq) => (
                      <Accordion.Item key={faq?.id} index={faq?.id!}>
                        <Accordion.Button>{faq?.question}</Accordion.Button>
                        <Accordion.Content>
                          <ContentfulRichText
                            color={textColor}
                            text={faq?.text}
                          />
                        </Accordion.Content>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </div>
              )}
            </Container>
          </Section>
        )
      })}
    </div>
  )
}

export const HowItWorksSectionsFragments = graphql`
  fragment HowItWorksSectionItemImage on ContentfulAsset {
    __typename
    id
    title
    gatsbyImageData(width: 377)
    file {
      details {
        image {
          width
          height
        }
      }
    }
  }
  fragment HowItWorksSectionItem on ContentfulHowItWorksSectionItem {
    __typename
    id
    title
    text {
      text
    }
    link {
      ...Link
    }
    image {
      ...HowItWorksSectionItemImage
    }
  }
  fragment HowItWorksSection on ContentfulHowItWorksSection {
    __typename
    id
    title
    label
    slug
    text {
      text
    }
    items {
      ...HowItWorksSectionItem
    }
    questions {
      ...Faq
    }
  }
  fragment HowItWorksSections on ContentfulHowItWorksSections {
    __typename
    id
    title
    sections {
      ...HowItWorksSection
    }
  }
`
